<script setup>
import { DsButton } from '@dss360/design-system'
import Divider from 'primevue/divider'
// import { useIsMobile } from '@/shared/composables/use-is-mobile.composable'
import { computed, defineAsyncComponent } from 'vue'
import DssAppearance
  from '@/core/components/dss-new-header/components/dss-appearance/dss-appearance.vue'
import DssDigitalLeaderHeader
  from '@/core/components/dss-new-header/components/dss-digital-leader-header/dss-digital-leader-header.vue'
import DssLanguageSwitcher
  from '@/core/components/dss-new-header/components/dss-new-language-switcher/dss-new-language-switcher.vue'
import DssSelectCompany
  from '@/core/components/dss-new-header/components/dss-select-company/dss-select-company.vue'
import { useUIStore } from '@/core/stores/ui.store'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import { BRADLEY_ANALYTICS_EVENTS } from '@/modules/new-bradley/analytics/bradley-analytics.constants.js'
import BradleyLogoIcon from '@/modules/new-bradley/components/bradley-logo-icon.vue'
import {
  isSupportRequestBlockedForCompany,
  isSupportRequestEnabled,
} from '@/modules/support/constants/support-feature.constant.js'
import { useAnalytics } from '@/shared/analytics'
import { useDssModal } from '@/shared/composables/dss-modal.composable.js'
import { useTheme } from '@/shared/composables/use-theme.composable'
import { isDigitalTeamEmail } from '@/shared/constants/digital-team.constant.js'

const authStore = useAuthStore()
const uiStore = useUIStore()
const { resolved } = useTheme()
const { trackEvent } = useAnalytics()
const { showModal, hideModal, isModalVisible } = useDssModal()

const ProjectRequestModal = defineAsyncComponent(() =>
  import('@/modules/platform-admin/components/project-request-modal/project-request-modal.vue'),
)

const SupportRequestModal = defineAsyncComponent(() =>
  import('@/modules/support/components/support-request-modal/support-request-modal.vue'),
)

// const { isMobile } = useIsMobile()

const isDigitalLeader = computed(() => authStore.isDigitalLeader && !authStore.isPlatformAdmin)
const isBradleyAccessEnabled = computed(() => {
  return authStore.tenantData?.enabledBradleyAccess || false
})

const canRequestNewProject = computed(() =>
  isDigitalTeamEmail(authStore.userData?.email),
)

const supportRequestEnabled = isSupportRequestEnabled()

const canUseSupportRequests = computed(() =>
  supportRequestEnabled
  && !isSupportRequestBlockedForCompany(authStore.tenantData, authStore.companyData),
)

function handleProjectRequestSuccess() {
  hideModal('project-request')
}

function handleSupportRequestSuccess() {
  hideModal('support-request')
}

function handleAskBradley() {
  const panelClosed = !uiStore.bradleyPanelOpen
  const resumeAfterMinimize = panelClosed && uiStore.bradleyFloatingWidgetVisible
  trackEvent(BRADLEY_ANALYTICS_EVENTS.ASK_BRADLEY_AI_BUTTON, { source: 'header' })
  uiStore.openBradleyPanel({ startNewChat: !resumeAfterMinimize })
}
</script>

<template>
  <header
    id="app-header"
    class="bg-dss-surface-0 flex items-center justify-between border-b border-b-dss-surface-200 p-2 lg:p-5 h-[86px] relative z-[11]"
  >
    <div class="h-full flex items-center gap-4">
      <!-- TODO: Will be implemented when we have a mobile version -->
      <!-- <sidebar-button-toggle v-if="isMobile" /> -->

      <router-link to="/">
        <img
          v-if="resolved === 'dark'"
          class="h-auto min-w-28 max-w-28 ml-2"
          src="@shared/assets/images/dss360-logotype-white.png"
          :alt="$t('common.logo.alt')"
        >
        <img
          v-else
          class="h-auto min-w-28 max-w-28 ml-2"
          src="@shared/assets/images/dss360-logotype-blue.png"
          :alt="$t('common.logo.alt')"
        >
      </router-link>

      <dss-digital-leader-header v-if="isDigitalLeader" />
      <dss-select-company v-else />

      <dss-button
        v-if="canRequestNewProject"
        :label="$t('platform-admin.project-request.new-request')"
        icon="pi pi-plus"
        data-testid="new-project-request-button"
        class="shrink-0 whitespace-nowrap"
        @click="showModal('project-request')"
      />

      <dss-button
        v-if="canUseSupportRequests"
        :label="$t('support.request.open-button')"
        icon="pi pi-question-circle"
        outlined
        kind="secondary"
        data-testid="support-request-button"
        class="shrink-0 whitespace-nowrap"
        @click="showModal('support-request')"
      />
    </div>

    <div class="flex items-center gap-4 lg:gap-5">
      <ds-button
        v-if="isBradleyAccessEnabled"
        severity="primary"
        rounded
        class="ask-bradley-header-btn w-auto! cursor-pointer border-0! px-4! py-2.5! text-white! shadow-none! [background:linear-gradient(97deg,#3333D6_-9.1%,#000014_108.94%)]! hover:[background:linear-gradient(97deg,#0000CC_-9.1%,#000029_108.94%)]! active:opacity-95"
        :aria-label="$t('core.header.ask-bradley')"
        @click="handleAskBradley"
      >
        <span class="inline-flex items-center gap-2">
          {{ $t('core.header.ask-bradley-prefix') }}
          <bradley-logo-icon
            class="ask-bradley-header-btn__icon"
            :height="16"
          />
        </span>
      </ds-button>
      <div class="flex items-center gap-2">
        <div class="flex items-center gap-4">
          <dss-session-countdown />

          <dss-notifications />

          <dss-support-requests v-if="canUseSupportRequests" />

          <dss-settings />

          <dss-avatar size="28px" />

          <Divider
            class="mx-0! py-4! w-[0.063rem] bg-dss-surface-200"
            layout="vertical"
          />
        </div>

        <div>
          <dss-language-switcher />
        </div>

        <dss-appearance />
      </div>
    </div>

    <dss-dialog
      :visible="isModalVisible('project-request')"
      class="bg-white w-[95dvw] max-w-4xl"
      :header="$t('platform-admin.project-request.title')"
      @update:visible="!$event && hideModal('project-request')"
    >
      <project-request-modal
        v-if="isModalVisible('project-request')"
        @close="hideModal('project-request')"
        @onsuccess="handleProjectRequestSuccess"
      />
    </dss-dialog>

    <dss-dialog
      :visible="isModalVisible('support-request')"
      class="bg-white w-[95dvw] max-w-4xl"
      :header="$t('support.request.title')"
      @update:visible="!$event && hideModal('support-request')"
    >
      <support-request-modal
        v-if="isModalVisible('support-request')"
        @close="hideModal('support-request')"
        @onsuccess="handleSupportRequestSuccess"
      />
    </dss-dialog>
  </header>
</template>

<style scoped>
.ask-bradley-header-btn :deep(.p-button-label),
.ask-bradley-header-btn :deep(.p-button-icon),
.ask-bradley-header-btn :deep(.pi),
.ask-bradley-header-btn__icon {
  color: #fff;
}
</style>
